import CloseIcon from '@mui/icons-material/Close';
import { Box, Drawer, IconButton, Stack, Typography, useTheme } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';

import { useScreenWidth } from '@/hooks/useScreenWidth';
import { useToast } from '@/components/Toast';
import useRenterInviteDrawerStore from '@/stores/useRenterInviteDrawerStore';
import RenterInviteFormContent from './RenterInviteFormContent';

const getDrawerProps = (isDesktop: boolean) =>
  isDesktop
    ? {
        headerPadding: { pt: 4, px: 4 },
        contentPadding: { pb: 4, px: 4 },
        paperWidth: '560px',
        anchor: 'right' as const,
        paperHeight: '100%',
        borderRadius: '0',
      }
    : {
        headerPadding: { pt: 3, px: 2 },
        contentPadding: { pb: 3, px: 2 },
        paperWidth: '100%',
        anchor: 'bottom' as const,
        paperHeight: 'min-content',
        borderRadius: '16px 16px 0 0',
      };

const RenterInviteDrawer = () => {
  const theme = useTheme();
  const { isDesktop } = useScreenWidth();
  const { isOpen, closeDrawer } = useRenterInviteDrawerStore();
  const queryClient = useQueryClient();
  const toast = useToast();

  const drawerProps = getDrawerProps(isDesktop);

  const handleSuccess = () => {
    closeDrawer();
    toast.success('Invitation sent successfully');
    queryClient.invalidateQueries({ queryKey: ['applications'] });
    queryClient.invalidateQueries({ queryKey: ['applications-pagination'] });
  };

  const backdrop = {
    id: 'renter-invite-drawer-backdrop',
    'data-testid': 'renter-invite-drawer-backdrop',
    style: {
      backgroundColor: isDesktop ? 'rgba(8, 10, 45, 0.1)' : 'rgba(8, 10, 45, 0.3)',
    },
  };

  return (
    <Drawer
      anchor={drawerProps.anchor}
      open={isOpen}
      onClose={() => {
        /* Drawer only closes via the close icon, not backdrop or Escape */
      }}
      slotProps={{ backdrop }}
      sx={{
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          borderBottom: 'none',
          borderRadius: drawerProps.borderRadius,
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          height: drawerProps.paperHeight,
          width: drawerProps.paperWidth,
          overflow: 'hidden',
          [theme.breakpoints.down('sm')]: {
            maxHeight: '90vh',
          },
        },
      }}
    >
      <Box sx={{ flexShrink: 0, ...drawerProps.headerPadding }}>
        <Box
          sx={{
            width: '100%',
            backgroundColor: 'background.default',
            pb: 2,
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="h4" sx={{ lineHeight: '46px' }}>
              Invite Applicant
            </Typography>
            <IconButton
              color="inherit"
              aria-label="close drawer"
              onClick={closeDrawer}
              sx={{ mr: '-12px' }}
              edge="start"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </Box>

      <Stack
        sx={{
          flex: 1,
          overflowY: 'auto',
          boxSizing: 'border-box',
          ...drawerProps.contentPadding,
          [theme.breakpoints.down('sm')]: {
            overflowX: 'hidden',
          },
        }}
      >
        <RenterInviteFormContent onSuccess={handleSuccess} />
      </Stack>
    </Drawer>
  );
};

export default RenterInviteDrawer;
